import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
  Cell,
} from 'recharts';

const GanttChart = ({ procedures }) => {
  const [selected, setSelected] = useState(null);

  // Custom tick component to truncate long names
  const CustomYAxisTick = ({ x, y, payload }) => {
    const maxLength = 60;
    const text = payload.value;
    const truncatedText =
      text.length > maxLength ? text.substring(0, maxLength) + '...' : text;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={4}
          textAnchor="end"
          fill="#1e293b"
          fontSize={12}
          fontWeight={600}
        >
          <title>{text}</title>
          {truncatedText}
        </text>
      </g>
    );
  };

  if (!procedures || procedures.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic">
        Nu există proceduri pentru a genera graficul.
      </p>
    );
  }

  const getDays = (start, end) =>
    Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  const projectStart = new Date(procedures[0].startDate);

  const data = procedures.map((proc, index) => {
    const start = new Date(proc.startDate);
    const end = new Date(proc.endDate);
    const offset = getDays(projectStart, start);
    const duration = getDays(start, end) || 1;
    return {
      name: proc.procedureTitle,
      offset,
      duration,
      label: `${proc.startDate} → ${proc.endDate}`,
      barColor:
        proc.barColor || (proc.type === 'future' ? '#a5b4fc' : '#6366f1'),
      labelColor: proc.labelColor || '#312e81',
      isFuture: proc.isFuture || proc.type === 'future',
      index: proc.index,
      cpvCode: proc.cpvCode,
      cpvName: proc.cpvName,
      estimatedValue: proc.estimatedValue,
      currency: proc.currency,
    };
  });

  const totalDays = Math.max(...data.map((d) => d.offset + d.duration)) + 1;

  const handleBarClick = (data, index) => {
    setSelected(data);
  };

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={data.length * 100 + 60}>
        <BarChart
          layout="vertical"
          data={data}
          margin={{ top: 20, right: 40, left: 0, bottom: 20 }}
          barCategoryGap={45}
          barSize={28}
        >
          <XAxis
            type="number"
            domain={[0, totalDays]}
            tickFormatter={(val) => `Ziua ${val}`}
            stroke="#9ca3af"
            tick={{ fontSize: 12, fill: '#6366f1', fontWeight: 600 }}
          />
          <YAxis
            dataKey="name"
            type="category"
            width={280}
            tick={<CustomYAxisTick />}
            stroke="#9ca3af"
            interval={0}
          />
          <Bar dataKey="offset" stackId="gantt" fill="#f3f4f6" />
          <Bar
            dataKey="duration"
            stackId="gantt"
            radius={[6, 6, 6, 6]}
            onClick={handleBarClick}
            minPointSize={5}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                cursor="pointer"
                fill={entry.barColor}
                opacity={selected?.name === entry.name ? 1 : 0.85}
                stroke={selected?.name === entry.name ? '#4338ca' : 'none'}
                strokeWidth={selected?.name === entry.name ? 2 : 0}
              />
            ))}
            <LabelList
              dataKey="label"
              position="right"
              style={{ fill: '#334155', fontSize: 13, fontWeight: 500 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legendă */}
      <div className="flex gap-6 mt-4 items-center text-sm">
        <span className="flex items-center gap-2">
          <span className="w-5 h-3 rounded bg-[#6366f1] border border-[#4338ca] inline-block" />
          Procedură actuală
        </span>
        <span className="flex items-center gap-2">
          <span className="w-5 h-3 rounded bg-[#a5b4fc] border border-[#6366f1] inline-block" />
          Procedură viitoare
        </span>
      </div>

      {selected && (
        <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-xl shadow-sm">
          <h3 className="font-semibold text-indigo-800 mb-1 flex items-center gap-2">
            {selected.name}
            {selected.isFuture && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-indigo-200 text-indigo-800 text-xs font-semibold border border-indigo-400">
                Estimată
              </span>
            )}
          </h3>
          <p className="text-sm text-indigo-700 italic">{selected.label}</p>
          {selected.cpvCode && (
            <p className="text-xs text-indigo-600 mt-1" title={selected.cpvName}>
              CPV: {selected.cpvCode}{selected.cpvName ? ` - ${selected.cpvName}` : ''}
            </p>
          )}
          {selected.estimatedValue > 0 && (
            <p className="text-xs text-green-700 font-semibold mt-1">
              Valoare: {selected.estimatedValue?.toLocaleString('ro-RO')} {selected.currency || 'RON'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GanttChart;
